import { __read } from "tslib";
import { NumberUtils } from '../../math';
import { PropertyClass } from '../types';
export function hexToRgba(hex, opacity) {
    var longHex = longFormatHex(hex);
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(longHex);
    return result
        ? [
            parseInt(result[1], 16) / 255,
            parseInt(result[2], 16) / 255,
            parseInt(result[3], 16) / 255,
            opacity || 1,
        ]
        : null;
}
// lossy (no alpha channel in hex)
export function rgbaToHex(_a) {
    var _b = __read(_a, 3), r = _b[0], g = _b[1], b = _b[2];
    var rHex = Math.round(r * 255).toString(16);
    var gHex = Math.round(g * 255).toString(16);
    var bHex = Math.round(b * 255).toString(16);
    // The result of each of the above toString(16) can be a single char: 0 to f.
    // In these cases, to create a valid long format HEX, they need to be padded with a leading 0.
    var pad = function (s) { return (s.length < 2 ? "0" + s : s); };
    return ("#" + pad(rHex) + pad(gHex) + pad(bHex)).toUpperCase();
}
export function isValidHex(hex) {
    return /^#(?:[0-9A-F]{3}|[0-9A-F]{6})$/i.test(hex);
}
/**
 * Converts an RGB color value to HSL. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes r, g, and b are contained in the set [0, 1] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param `RgbaColor`
 * @returns `HslaColor` representation
 */
export function rgbaToHsla(_a) {
    var _b = __read(_a, 4), r = _b[0], g = _b[1], b = _b[2], a = _b[3];
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var h = 0;
    var s = 0;
    var l = (max + min) / 2;
    if (max === min) {
        h = 0;
        s = 0;
    }
    else {
        var d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
        }
        h /= 6;
    }
    return [h, s, l, a];
}
/**
 * Converts an HSLA color value to RGBA. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSL_color_space.
 * Assumes h, s, and l are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 1].
 *
 * @param `HslaColor`
 * @returns `RgbaColor` representation
 */
export function hslaToRgba(_a) {
    var _b = __read(_a, 4), h = _b[0], s = _b[1], l = _b[2], a = _b[3];
    var r;
    var g;
    var b;
    var hue2rgb = function (p, q, t) {
        // eslint-disable-next-line no-param-reassign
        if (t < 0)
            t += 1;
        // eslint-disable-next-line no-param-reassign
        if (t > 1)
            t -= 1;
        if (t < 1 / 6)
            return p + (q - p) * 6 * t;
        if (t < 1 / 2)
            return q;
        if (t < 2 / 3)
            return p + (q - p) * (2 / 3 - t) * 6;
        return p;
    };
    if (s === 0) {
        r = l;
        g = l;
        b = l;
    }
    else {
        var q = l < 0.5 ? l * (1 + s) : l + s - l * s;
        var p = 2 * l - q;
        r = hue2rgb(p, q, h + 1 / 3);
        g = hue2rgb(p, q, h);
        b = hue2rgb(p, q, h - 1 / 3);
    }
    return [r, g, b, a];
}
/**
 * Converts an RGBA color value to HSBA. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSV_color_space.
 * Assumes r, g, and b are contained in the set [0, 1] and
 * returns h, s, and l in the set [0, 1].
 *
 * @param `RgbaColor`
 * @returns `HsbaColor` representation
 */
export function rgbaToHsba(_a) {
    var _b = __read(_a, 4), r = _b[0], g = _b[1], b = _b[2], a = _b[3];
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var h = 0;
    var s = 0;
    var v = max;
    var d = max - min;
    if (max === 0) {
        s = 0;
    }
    else {
        s = d / max;
    }
    if (max === min) {
        h = 0; // achromatic
    }
    else {
        switch (max) {
            case r:
                h = (g - b) / d + (g < b ? 6 : 0);
                break;
            case g:
                h = (b - r) / d + 2;
                break;
            case b:
                h = (r - g) / d + 4;
                break;
            default:
        }
        h /= 6;
    }
    return [h, s, v, a];
}
/**
 * Converts an HSBA color value to RGBA. Conversion formula
 * adapted from http://en.wikipedia.org/wiki/HSV_color_space.
 * Assumes h, s, and b are contained in the set [0, 1] and
 * returns r, g, and b in the set [0, 1].
 *
 * @param `HsbaColor`
 * @returns `RgbaColor` representation
 */
export function hsbaToRgba(_a) {
    var _b = __read(_a, 4), h = _b[0], s = _b[1], v = _b[2], a = _b[3];
    var r = 0;
    var g = 0;
    var b = 0;
    var i = Math.floor(h * 6);
    var f = h * 6 - i;
    var p = v * (1 - s);
    var q = v * (1 - f * s);
    var t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0:
            r = v;
            g = t;
            b = p;
            break;
        case 1:
            r = q;
            g = v;
            b = p;
            break;
        case 2:
            r = p;
            g = v;
            b = t;
            break;
        case 3:
            r = p;
            g = q;
            b = v;
            break;
        case 4:
            r = t;
            g = p;
            b = v;
            break;
        case 5:
            r = v;
            g = p;
            b = q;
            break;
        default:
    }
    return [r, g, b, a];
}
/**
 * Given any string, returns a valid long form hex value. When a valid
 * hex char is encountered (0-1, a-f, A-F) the hex value is created from the longest valid substring
 * that starts with that char.
 * @param text - the input string
 * @retuns a hex value in a valid long form: #XXXXXX or `null` if parsing failed
 *
 * @example
 *
 * parseHex('ZZE2Z') // '#E2E2E2`
 */
export function parseHex(text) {
    var _a;
    var regexMatch = (_a = text.match(/[A-Fa-f0-9]{1,6}/)) === null || _a === void 0 ? void 0 : _a[0];
    if (!regexMatch)
        return null;
    var match = regexMatch.toUpperCase();
    var matchLength = match.length;
    if (matchLength === 6)
        return "#" + match;
    if (matchLength >= 3 && matchLength <= 5) {
        return "#" + match
            .slice(0, 3)
            .split('')
            .map(function (c) { return "" + c + c; })
            .join('');
    }
    if (matchLength === 2)
        return "#" + match.repeat(3);
    if (matchLength === 1)
        return "#" + match.repeat(6);
    return null;
}
export function isColor(object) {
    return object.className === PropertyClass.Color;
}
export function isCloseTo(colorA, colorB, precision) {
    return colorA.rgbaValues.every(function (valueA, i) {
        return NumberUtils.isCloseTo(valueA, colorB.rgbaValues[i], precision);
    });
}
// convert shorthand hex into long form: #123 => #112233
export function longFormatHex(hex) {
    return hex.length === 4 ? hex.replace(/^#(.)(.)(.)$/, '#$1$1$2$2$3$3') : hex;
}
